/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { useFormik, type FormikHelpers } from 'formik'
import isEqual from 'lodash/isEqual'
import { useMemo, type FC } from 'react'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

const validationSchema = yup.object().shape({
  name: yup.string().required('O nome é obrigatório').default('').min(1).max(255),
  username: yup.string().email().required('O endereço de email é obrigatório').default(''),
  password: yup.string().nullable(),
  newsletter: yup.bool().nullable(),
  document: yup.string().nullable().min(11).max(14),
  phoneNumber: yup.string().nullable().min(10).max(11),
  birthDate: yup.mixed().oneOf([
    yup.date().nullable(),
    yup.string().nullable()
  ]).nullable(),
  status: yup.string().oneOf([
    'confirmation_pending',
    'active',
    'inactive',
    'blocked',
    'password_recovery',
    'deleted',
    'delete_pending'
  ]).default('confirmation_pending'),
  role: yup.string().oneOf([
    'customer',
    'fiscal',
    'editor',
    'admin',
    'human_resources',
    'physical_store_manager'
  ]).default('customer')
})

export type ValidationSchema = yup.InferType<typeof validationSchema>

export type FormUserHandleSubmit<T = void> = (
  values: ValidationSchema,
  helpers: FormikHelpers<ValidationSchema>
) => T | Promise<T>

export type Props = {
  onSubmit?: FormUserHandleSubmit,
  initialValues?: ValidationSchema,
  isLoading?: boolean
}

/**
 * FormPage is described here.
 *
 * ## Usage
 * ```js
 * import * as React from 'react'
 * import FormPage from './src/views/PostEdit/FormPage.tsx'
 *
 * const MyComponent = () => (
 *   <FormPage />
 * )
 *
 * export default MyComponent
 * ```
 */
const FormPage: FC<Props> = ({
  onSubmit = () => { },
  initialValues = {},
  isLoading = false
}) => {
  const navigate = useNavigate()
  const formik = useFormik({
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    enableReinitialize: true,
    validationSchema,
    initialValues: validationSchema.cast(initialValues),
    onSubmit
  })

  const isChanged: boolean = useMemo(() => {
    return !isEqual(formik?.values, formik.initialValues)
  }, [formik?.values, formik.initialValues])

  return (
    <Form className='mb-3 mt-3' onSubmit={formik.handleSubmit}>
      <Row>
        <Form.Group className="mb-3" as={Col} md={12}>
          <Form.Label>Nome do usuário</Form.Label>
          <Form.Control
            type='text'
            placeholder='Ex: João da Silva'
            name='name'
            value={formik?.values?.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group className="mb-3" as={Col} md={12}>
          <Form.Label>Endereço de email</Form.Label>
          <Form.Control
            type='text'
            placeholder='Ex: joaodasilva@exemplo.com'
            name='username'
            value={formik?.values?.username}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group className="mb-3" as={Col} md={12}>
          <Form.Label>Senha</Form.Label>
          <Form.Control
            type='password'
            placeholder='Ex: *****'
            name='password'
            value={formik?.values?.password ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
          />
        </Form.Group>
        <Form.Group className="mb-3" as={Col} md={12}>
          <Form.Label>Situação</Form.Label>
          <Form.Select
            value={formik?.values?.status}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading}
            name="status"
          >
            <option>Selecione uma opção</option>
            <option value='confirmation_pending'>Pendente de confirmação</option>
            <option value='active'>Ativo</option>
            <option value='inactive'>Inativo</option>
            <option value='blocked'>Bloqueado</option>
            <option value='password_recovery'>Em Recuperação de senha</option>
            <option value='deleted'>Excluído</option>
            <option value='delete_pending'>Pendente de exclusão</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3" as={Col} md={12}>
          <Form.Label>Tipo de usuário</Form.Label>
          <Form.Select
            value={formik?.values?.role}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={formik.isSubmitting || isLoading || formik.values.role === 'admin'}
            name="role"
          >
            <option>Selecione uma opção</option>
            <option value='customer'>Cliente</option>
            <option value='fiscal'>Fiscal</option>
            <option value='editor'>Editor</option>
            <option value='human_resources'>Recursos Humano</option>
            <option value='physical_store_manager'>Gerente de Loja Física</option>
            <option disabled value='admin'>Administrador</option>
          </Form.Select>
        </Form.Group>
        <Col md={12}>
          <Button
            variant="link"
            style={{ marginRight: 10 }} onClick={() => navigate('/users')}
            disabled={formik.isSubmitting || isLoading}
          >
            Voltar
          </Button>
          <Button
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting || !isChanged || isLoading}
          >
            Salvar
          </Button>
        </Col>
      </Row>
    </Form>
  )
}

export default FormPage
